import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getMeetings } from '../../features/meeting/meetingSlice';
import { useNavigate } from 'react-router-dom'; 
import { toDate } from '../../utils/meetingUtils';

function MeetingSearch({ onFilteredMeetings, onMeetingSelect, onRefresh, onSearch }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredMeetings, setFilteredMeetings] = useState([]);
    const [selectedMeetingId, setSelectedMeetingId] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate(); 
    
    const { meetings } = useSelector((state) => state.meeting);
    const isVisibleMeeting = (m) => m?.state !== false && m?.state !== 'false';

    useEffect(() => {
        setFilteredMeetings((meetings || []).filter(isVisibleMeeting));
    }, [meetings]);

    useEffect(() => {
        if (typeof onFilteredMeetings === 'function') {
            onFilteredMeetings(filteredMeetings);
        }
    }, [filteredMeetings]);

    const highlightText = (text, searchTerm) => {
        if (!searchTerm.trim()) return text;
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, index) => 
            regex.test(part) ? <strong key={index}>{part}</strong> : part
        );
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (typeof onSearch === 'function') {
            onSearch('');
        }
    };

    const handleSearchSubmit = () => {
        if (typeof onSearch === 'function') {
            onSearch(searchTerm);
        }
    };

    const handleRefresh = () => {
        if (typeof onRefresh === 'function') {
            onRefresh();
            return;
        }
        dispatch(getMeetings());
    };

    const handleMeetingClick = (meeting) => {
        setSelectedMeetingId(meeting.id);
        if (onMeetingSelect) {
            onMeetingSelect(meeting);
        }
        navigate(`/meeting/${meeting.id}`); 
    };

    return (
        <div className="row mb-4">
            <div className="col-xl-12">
                <div className="card">
                    <div className="card-body">
                        <div className="row align-items-center g-3">
                            <div className="col-xl-8 col-lg-7 col-md-12">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="fa fa-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Toplantı başlığında ara..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSearchSubmit();
                                            }
                                        }}
                                    />
                                    {searchTerm && (
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={handleClearSearch}
                                            title="Aramayı temizle"
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="col-xl-4 col-lg-5 col-md-12">
                                <div className="d-flex justify-content-end align-items-center flex-wrap gap-2">
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={handleSearchSubmit}
                                        title="Ara"
                                    >
                                        <i className="fa fa-search me-2"></i>
                                        Ara
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleRefresh}
                                        title="Toplantıları yenile"
                                    >
                                        <i className="fa fa-refresh me-2"></i>
                                        Yenile
                                    </button>
                                    <span className="text-muted badge bg-light text-dark">
                                        {filteredMeetings.length} toplantı
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        {searchTerm && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="alert alert-info py-2 mb-0">
                                        <small>
                                            "<strong>{searchTerm}</strong>" için {filteredMeetings.length} sonuç bulundu
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )}

                        {filteredMeetings.length > 0 && searchTerm && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="list-group">
                                        {filteredMeetings.map((meeting, index) => (
                                            <div 
                                                key={meeting.id || index} 
                                                className={`list-group-item list-group-item-action ${
                                                    selectedMeetingId === meeting.id ? 'active' : ''
                                                }`}
                                                onClick={() => handleMeetingClick(meeting)}
                                                style={{ 
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (selectedMeetingId !== meeting.id) {
                                                        e.target.style.backgroundColor = '#f8f9fa';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedMeetingId !== meeting.id) {
                                                        e.target.style.backgroundColor = '';
                                                    }
                                                }}
                                            >
                                                <div className="d-flex w-100 justify-content-between align-items-start">
                                                    <div>
                                                        <h6 className="mb-1">
                                                            {highlightText(meeting.title, searchTerm)}
                                                        </h6>
                                                        {meeting.meetingDescription && (
                                                            <p className="mb-1 text-muted small">
                                                                {meeting.meetingDescription.length > 100 
                                                                    ? meeting.meetingDescription.substring(0, 100) + '...' 
                                                                    : meeting.meetingDescription
                                                                }
                                                            </p>
                                                        )}
                                                        {meeting.startDate && toDate(meeting.startDate) && (
                                                            <small className="text-muted">
                                                                <i className="fa fa-calendar me-1"></i>
                                                                {toDate(meeting.startDate).toLocaleDateString('tr-TR', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                    day: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </small>
                                                        )}
                                                    </div>
                                                    <div className="text-end">
                                                        {selectedMeetingId === meeting.id && (
                                                            <i className="fa fa-check text-white"></i>
                                                        )}
                                                        {meeting.status && (
                                                            <span className={`badge ms-2 ${
                                                                meeting.status === 'completed' ? 'bg-success' :
                                                                meeting.status === 'cancelled' ? 'bg-danger' :
                                                                'bg-warning'
                                                            }`}>
                                                                {meeting.status === 'completed' ? 'Tamamlandı' :
                                                                 meeting.status === 'cancelled' ? 'İptal Edildi' :
                                                                 ''}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MeetingSearch;
